import { DomainError } from "./errors";
import { isValidCNPJ, isValidCPF, normalizeDocument } from "./documents";
import type { Uf } from "./dashboard";
import {
  HOMOLOG_RECIPIENT_NAME,
  NFE_SOFTWARE,
  NFE_VERSION,
  UF_IBGE,
  accessKey,
  centsToDecimal,
  checkNfeTotals,
  icmsGroup,
  isNfePaymentType,
  nfeAddressGroup,
  nfeDateTime,
  nfeDocField,
  nfeIbsCbsGroup,
  nfeIbsCbsTot,
  nfeIpiGroup,
  nfePisCofinsGroup,
  nfeLotFields,
  nfeProblems,
  q4ToDecimal,
  type NfeDet,
  type NfeIssuer,
  type NfeItem,
} from "./nfe";
import { sha1Hex } from "./sha1";
import type { TaxResult } from "./tax";

// ===== NFC-e modelo 65 (RF-VEN-05; T3.1) =====
//
// Mesmo leiaute 4.00 da NF-e com as regras do consumidor final presencial: tpImp 4 (DANFE NFC-e), indFinal 1,
// indPres 1, sem endereço do destinatário (CPF/CNPJ opcional), modFrete 9, troco (vTroco) e, em contingência offline,
// tpEmis 9 com dhCont/xJust. O QR Code (versão 2) usa o CSC/ID token da empresa.

/** Texto exigido na descrição do primeiro item em homologação (NFC-e). */
export const NFCE_HOMOLOG_ITEM = "NOTA FISCAL EMITIDA EM AMBIENTE DE HOMOLOGACAO - SEM VALOR FISCAL";
/** Prazo do cancelamento da NFC-e (padrão nacional de 30 minutos). */
export const NFCE_CANCEL_WINDOW_MINUTES = 30;
/** Prazo padrão para transmitir a NFC-e em contingência offline (horas). */
export const NFCE_CONTINGENCY_HOURS = 24;
export const NFCE_QR_VERSION = "2";

export type NfceConsumer = { document: string | null; name: string | null };
export type NfcePayment = {
  sefazType: string | null;
  name: string;
  /** Valor entregue pelo cliente (inclui o troco no dinheiro). */
  amountCents: number;
};

export type NfceInput = {
  environment: 1 | 2;
  series: number;
  number: number;
  cnf: string;
  /** 1 normal; 9 contingência offline. */
  emissionType: 1 | 9;
  contingency: { at: Date; reason: string } | null;
  issuedAt: Date;
  issuer: NfeIssuer;
  consumer: NfceConsumer | null;
  items: NfeItem[];
  taxes: TaxResult;
  payments: NfcePayment[];
  complementaryInfo: string | null;
  saleNumber: string | null;
};

export type NfceInfNFe = {
  versao: string;
  Id: string;
  ide: Record<string, string | number>;
  emit: Record<string, unknown>;
  dest?: Record<string, unknown>;
  det: NfeDet[];
  total: { ICMSTot: Record<string, number>; IBSCBSTot?: Record<string, unknown> };
  transp: { modFrete: 9 };
  pag: { detPag: Record<string, unknown>[]; vTroco?: number };
  infAdic?: { infCpl: string };
};

const d2 = centsToDecimal;
const clip = (s: string, n: number) => s.replace(/\s+/g, " ").trim().slice(0, n);

/** O que impede a NFC-e (mensagens pt-BR). Reaproveita as regras da NF-e para emitente e itens. */
export function nfceProblems(i: Omit<NfceInput, "taxes" | "cnf" | "issuedAt">): string[] {
  const placeholder = {
    document: "52998224725",
    name: "Consumidor",
    ieIndicator: 9 as const,
    ie: null,
    email: null,
    phone: null,
    address: i.issuer.address,
  };
  const p = nfeProblems({
    environment: i.environment,
    series: i.series,
    number: i.number,
    emissionType: 1,
    natureName: "Venda",
    purpose: 1,
    presence: 1,
    issuer: i.issuer,
    recipient: placeholder,
    items: i.items,
    transport: { mode: 9, carrier: null, vehicle: null, volumes: [] },
    payments: [],
    noPayment: false,
    complementaryInfo: null,
    fiscoInfo: null,
    authorizedXml: [],
    orderNumber: null,
  }).filter((m) => !m.startsWith("A nota aceita até"));
  const doc = normalizeDocument(i.consumer?.document ?? "");
  if (doc && !(doc.length === 11 ? isValidCPF(doc) : isValidCNPJ(doc)))
    p.push("Consumidor: CPF/CNPJ inválido");
  if (i.emissionType === 9) {
    const why = i.contingency?.reason.trim() ?? "";
    if (!i.contingency || why.length < 15)
      p.push("Contingência sem data/hora ou justificativa (mínimo 15 caracteres)");
  }
  if (i.payments.length === 0) p.push("A NFC-e precisa de ao menos um pagamento");
  return p;
}

/** detPag + vTroco: agrupa por tPag; falta vai para o último; troco = entregue − vNF. */
export function nfcePayments(payments: readonly NfcePayment[], invoiceCents: number) {
  type G = { tPag: string; xPag?: string; cents: number };
  const groups: G[] = [];
  for (const p of payments) {
    const tPag = isNfePaymentType(p.sefazType) && p.sefazType !== "90" ? p.sefazType : "99";
    const xPag = tPag === "99" ? (p.name.trim() || "Outros").slice(0, 60).padEnd(2, ".") : undefined;
    const g = groups.find((x) => x.tPag === tPag && x.xPag === xPag);
    if (g) g.cents += p.amountCents;
    else groups.push({ tPag, ...(xPag ? { xPag } : {}), cents: p.amountCents });
  }
  const sum = groups.reduce((a, g) => a + g.cents, 0);
  if (sum < invoiceCents) groups[groups.length - 1]!.cents += invoiceCents - sum;
  const change = Math.max(0, sum - invoiceCents);
  const detPag = groups.map((g) => ({
    indPag: 0,
    tPag: g.tPag,
    ...(g.xPag ? { xPag: g.xPag } : {}),
    vPag: d2(g.cents),
    // Cartão sem integração com o sistema (POS): tpIntegra 2. TEF fica para depois.
    ...(g.tPag === "03" || g.tPag === "04" ? { card: { tpIntegra: 2 } } : {}),
  }));
  return { detPag, ...(change > 0 ? { vTroco: d2(change) } : {}) };
}

/** Monta o `infNFe` da NFC-e. Lança `nfe_incomplete` ou `nfe_totals_mismatch`. */
export function buildNfcePayload(i: NfceInput): { accessKey: string; infNFe: NfceInfNFe } {
  const problems = nfceProblems(i);
  if (i.taxes.items.length !== i.items.length) problems.push("Impostos não correspondem aos itens");
  if (i.taxes.destination !== 1) problems.push("NFC-e só em operação interna");
  if (problems.length) throw new DomainError("nfe_incomplete", problems.join("; "));
  const tot = checkNfeTotals(i.taxes);
  const uf = i.issuer.address.uf as Uf;
  const key = accessKey({
    uf,
    issuedAt: i.issuedAt,
    cnpj: i.issuer.cnpj,
    model: 65,
    series: i.series,
    number: i.number,
    emissionType: i.emissionType,
    cnf: i.cnf,
  });
  const ide: NfceInfNFe["ide"] = {
    cUF: UF_IBGE[uf],
    cNF: i.cnf,
    natOp: "Venda ao consumidor",
    mod: 65,
    serie: i.series,
    nNF: i.number,
    dhEmi: nfeDateTime(i.issuedAt),
    tpNF: 1,
    idDest: 1,
    cMunFG: i.issuer.address.cityIbge,
    tpImp: 4,
    tpEmis: i.emissionType,
    cDV: Number(key[43]),
    tpAmb: i.environment,
    finNFe: 1,
    indFinal: 1,
    indPres: 1,
    indIntermed: 0,
    procEmi: 0,
    verProc: NFE_SOFTWARE,
    ...(i.emissionType === 9 && i.contingency
      ? { dhCont: nfeDateTime(i.contingency.at), xJust: clip(i.contingency.reason, 256) }
      : {}),
  };
  const emit = {
    CNPJ: normalizeDocument(i.issuer.cnpj),
    xNome: clip(i.issuer.legalName, 60),
    ...(i.issuer.tradeName?.trim() ? { xFant: clip(i.issuer.tradeName, 60) } : {}),
    enderEmit: nfeAddressGroup(i.issuer.address, i.issuer.phone),
    IE: i.issuer.ie,
    CRT: i.issuer.crt,
  };
  const doc = normalizeDocument(i.consumer?.document ?? "");
  const name = i.consumer?.name?.trim();
  const dest = doc
    ? {
        ...nfeDocField(doc),
        ...(i.environment === 2 ? { xNome: HOMOLOG_RECIPIENT_NAME } : name ? { xNome: clip(name, 60) } : {}),
        indIEDest: 9,
      }
    : undefined;
  const det: NfeDet[] = i.taxes.items.map((t, k) => {
    const it = i.items[k]!;
    const gtin = it.gtin ?? "SEM GTIN";
    const ipi = nfeIpiGroup(t);
    const ibs = nfeIbsCbsGroup(t);
    const unit = clip(it.unit, 6) || "UN";
    const lot = nfeLotFields(it, t.infoText);
    return {
      nItem: k + 1,
      prod: {
        cProd: clip(it.code, 60) || String(k + 1),
        cEAN: gtin,
        xProd: k === 0 && i.environment === 2 ? NFCE_HOMOLOG_ITEM : clip(it.description, 120),
        NCM: it.ncm!,
        ...(it.cest && /^\d{7}$/.test(it.cest) ? { CEST: it.cest } : {}),
        CFOP: t.cfop,
        uCom: unit,
        qCom: q4ToDecimal(it.qtyQ4),
        vUnCom: d2(it.unitPriceCents),
        vProd: d2(t.grossCents),
        cEANTrib: gtin,
        uTrib: unit,
        qTrib: q4ToDecimal(it.qtyQ4),
        vUnTrib: d2(it.unitPriceCents),
        ...(t.discountCents ? { vDesc: d2(t.discountCents) } : {}),
        ...(t.otherCents ? { vOutro: d2(t.otherCents) } : {}),
        indTot: 1,
        ...lot.rastro,
      },
      imposto: {
        vTotTrib: d2(t.approxTaxCents),
        ICMS: icmsGroup(t.icms),
        ...(ipi ? { IPI: ipi } : {}),
        PIS: nfePisCofinsGroup(t.pis, "PIS"),
        COFINS: nfePisCofinsGroup(t.cofins, "COFINS"),
        ...(ibs ? { IBSCBS: ibs } : {}),
      },
      ...lot.info,
    };
  });
  const ICMSTot = Object.fromEntries(Object.entries(tot).map(([k, v]) => [k, d2(v)]));
  const ibsTot = nfeIbsCbsTot(i.taxes.items);
  const parts: string[] = [];
  if (i.complementaryInfo?.trim()) parts.push(i.complementaryInfo.trim());
  if (tot.vTotTrib > 0)
    parts.push(
      `Valor aproximado dos tributos: R$ ${(tot.vTotTrib / 100).toFixed(2).replace(".", ",")} (Lei 12.741/2012)`,
    );
  if (i.saleNumber) parts.push(`Venda PDV ${i.saleNumber}`);
  const infCpl = clip(parts.join(" | "), 5000);
  const infNFe: NfceInfNFe = {
    versao: NFE_VERSION,
    Id: `NFe${key}`,
    ide,
    emit,
    ...(dest ? { dest } : {}),
    det,
    total: { ICMSTot, ...(ibsTot ? { IBSCBSTot: ibsTot } : {}) },
    transp: { modFrete: 9 },
    pag: nfcePayments(i.payments, tot.vNF),
    ...(infCpl ? { infAdic: { infCpl } } : {}),
  };
  return { accessKey: key, infNFe };
}

// ---------- QR Code (versão 2, NT 2015.002 v1.60+) ----------

export type NfceQrInput = {
  /** URL de consulta por QR Code da UF/ambiente. */
  url: string;
  accessKey: string;
  environment: 1 | 2;
  /** Identificador do CSC (sem zeros à esquerda). */
  cscId: string;
  csc: string;
  /** Contingência offline: dia da emissão, vNF e DigestValue da assinatura. */
  offline?: { issuedAt: Date; totalCents: number; digestValue: string };
};

const hexOfAscii = (s: string) =>
  Array.from(s, (c) => c.charCodeAt(0).toString(16).padStart(2, "0")).join("");

/** Texto do QR Code: `url?p=chave|2|tpAmb|cIdToken|hash` (online) ou com dia, vNF e digVal (offline). */
export function nfceQrCode(q: NfceQrInput): string {
  const id = String(Number(q.cscId));
  const fields = q.offline
    ? [
        q.accessKey,
        NFCE_QR_VERSION,
        String(q.environment),
        nfeDateTime(q.offline.issuedAt).slice(8, 10),
        d2(q.offline.totalCents).toFixed(2),
        hexOfAscii(q.offline.digestValue),
        id,
      ]
    : [q.accessKey, NFCE_QR_VERSION, String(q.environment), id];
  const base = fields.join("|");
  const hash = sha1Hex(base + q.csc).toUpperCase();
  return `${q.url}?p=${base}|${hash}`;
}

export const isValidCsc = (csc: string) => /^[0-9A-Za-z]{16,36}$/.test(csc);
export const isValidCscId = (id: string) => /^\d{1,6}$/.test(id) && Number(id) > 0;

/** Prazo do cancelamento desde a autorização. */
export const nfceCancelDeadline = (authorizedAtMs: number): Date =>
  new Date(authorizedAtMs + NFCE_CANCEL_WINDOW_MINUTES * 60_000);
